import { Box, Card, Stack, Typography, useMediaQuery } from '@mui/material';
import axios from 'axios';
import moment from 'moment';

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLocation, useNavigate } from 'react-router';
import DeletePopupModal from 'src/components/common-modals/delete-modal';
import { disabledPlannerOnMds } from 'src/layouts/components/utils-method';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { paths, permissions } from 'src/paths';
import {
  applySelectedTemplateAction,
  copyTherapyWeekActionRest,
  get7DayLookBack,
  getTherapyPlanningData_2_0,
  getTherapyPlanningRangeAction,
  getPayerDayCountAction,
  getCmgStatus,
  getTherapyPlanningSummary_2_0,
  resetApplySelectedTherapyTemplate,
  resetCheckSplitMinInPlanner,
  resetDeleteMdsAssessmentByDate,
  resetSingleCellReducer,
  resetTherapyPlanningData_2_0,
  resetCmgStatus,
  resetTherapyPlanningDischargeDate,
  resetTherapyTemplatesAction,
  saveTherapyTemplatesAction,
} from 'src/redux/actions/therapyPlanning';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import useProgressNoteDue from '../progressNoteDue/useProgressNoteDue';
import GracePeriodAlert from './GracePeriodAlert';
import SummaryCard_2_0 from './SummaryCard_2.0';
import TherapyCard_2_0 from './TherapyCard_2_0';
import { useTherapyPlanning } from './therapyPlanningProvider';
import UncoveredDay from './uncoveredDay_2.0';
import { confirmCancelTestIds, dayTestId, therapyPlanningTestId } from './test-ids';

const { therapyPlanning } = paths;

const { UPDATE_THERAPIST_PLANNING } = permissions;

const uncoveredDumpyData = Array.from({ length: 9 }).map((res) => {
  return {
    isUncoveredDay: false,
  };
});

// THIN-8179: the episode start arrives as a UTC-midnight instant; its calendar date is the value,
// so the time part is dropped before formatting, or every US timezone shows the day before.
function formatEpisodeStartDate(eocStartDate: string | null | undefined): string | null {
  if (!eocStartDate) return null;
  return moment(eocStartDate.split('T')[0]).format('M/D/YY');
}

// THIN-8150: day 100 may never fall on a date — with 100 or more prior days the payer period starts past it.
export function payerDay100BannerText(blocked: {
  day100Date?: string | null;
  payerStartDate?: string | null;
}): string {
  if (blocked.day100Date) {
    return `Day 100 was ${moment(blocked.day100Date).format(
      'MMM D, YYYY'
    )}. Add or change the payer to continue planning.`;
  }
  return `Day 100 was already used before this payer started on ${moment(
    blocked.payerStartDate
  ).format('MMM D, YYYY')}. Add or change the payer to continue planning.`;
}

export const AlertIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M8.99982 6.7531V9.7531M8.99982 12.7531H9.00732M7.9613 2.92189L1.79264 13.5769C1.45049 14.1679 1.27941 14.4633 1.30469 14.7059C1.32675 14.9174 1.43758 15.1096 1.60959 15.2347C1.80681 15.3781 2.14826 15.3781 2.83115 15.3781H15.1685C15.8514 15.3781 16.1928 15.3781 16.39 15.2347C16.5621 15.1096 16.6729 14.9174 16.6949 14.7059C16.7202 14.4633 16.5491 14.1679 16.207 13.5769L10.0383 2.92189C9.6974 2.33302 9.52694 2.03858 9.30454 1.93969C9.11055 1.85344 8.88908 1.85344 8.69509 1.93969C8.47269 2.03858 8.30223 2.33302 7.9613 2.92189Z"
        stroke="#B54708"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * The `message` of a failure payload the server sent (`{ code, message }`), or '' when there is none.
 * A thrown `Error` (e.g. no response at all) carries a JS message, not one meant for the user.
 */
const serverRefusalMessage = (payload: any): string =>
  payload && !(payload instanceof Error) && typeof payload.message === 'string'
    ? payload.message
    : '';

const WeekView_2_0 = () => {
  const { weekStartDate, setWeekStartDate, setWeekEndDate, weekEndDate } = useTherapyPlanning();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const therapyPlanningAbort: any = axios.CancelToken.source();

  const [uncoveredDay, setUncoveredDay] = useState(uncoveredDumpyData);

  const [currentsDayList, setCurrentDayList] = useState<any>([]);
  const isTabletScreen = useMediaQuery((theme: any) => theme.breakpoints.down('xl'));
  const macbookScreen = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
  const [message, setMessage] = useState('');
  const updateTherapyPlanningResponse = useSelector(
    (state: any) => state?.updateTherapyPlanningReducer
  );
  const updateTherapyPlanningResponseDischargeDate = useSelector(
    (state: any) => state?.updateTherapyPlanningDischargeDateReducer
  );

  const therapyPlanning_2_0 = useSelector((state: any) => state.getTherapyPlanningDataReducer_2_0);
  const therapyCaseMixTemplatesEnabled = useSelector(
    (state: any) => state?.getPdpmFeatureToggleReducer?.response?.data
  );
  const therapyPlanningSummary = useSelector(
    (state: any) => state.getTherapyPlanningSummaryReducer_2_0
  );
  const getDischargeDatesOfTherapyPlanning2_0 = useSelector(
    (state: any) => state.getTherapyPlanningDischargeDateReducer_2_0?.response?.data
  );
  const dateOfServiceResponse = useSelector(
    (state: any) => state?.getDateOfServiceReducer?.response?.data
  );
  // THIN-8150: the server marks a date blocked only when the tenant's day-100 toggle is on.
  const payerDay100Block = useSelector(
    (state: any) =>
      state?.getPayerDayCountReducer?.response?.data?.find((item: any) => item?.blocked)
  );

  const [currentsDayDataList, setCurrentDayDataList] = useState<any>([]);
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const assessmentDetails = useSelector(
    (state: any) => state?.getAssessmentReducer?.response?.data
  );
  const getMultipleMdsAssessment = useSelector(
    (state: any) => state.getMultipleMDSAssessment?.response
  );

  const [multipleMdsAssessmentDates, setMultipleMdsAssessmentDates] = useState<any>([]);
  const assessmentDate = assessmentDetails?.assessmentDate
    ? assessmentDetails?.assessmentDate?.split('T')?.at(0)
    : '';
  const selectedPatient = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);
  const getMdsAssessmentRange = useSelector(
    (state: any) => state.getMdsAssessmentRangeReducer?.response?.data?.assessmentsByDate
  );
  const getTherapyPlanningLookBack = useSelector(
    (state: any) => state.getTherapyPlanning7dayLookBack
  );

  const constGetMdsAssessMentDate = (multipleMds: any) => {
    return multipleMds?.map((mds: any) => {
      const date = mds?.isFinalMds ? mds?.finalMds?.assessmentDate : mds?.assessmentDate;
      return date ? date?.split('T')?.at(0) : '';
    });
  };

  const checkTherapyTemplate = useSelector((state: any) => state.checkSplitMinInPlanner);
  const [deleteModalInfo, setDeleteModalInfo] = useState<any>({
    open: false,
    message: '',
    saveLabel: '',
    closeLabel: '',
    title: '',
    deleteType: '',
    errorType: '',
  });

  useMemo(() => {
    if (getMultipleMdsAssessment?.data?.length) {
      const getOpenEoc = getMultipleMdsAssessment?.data?.filter(
        (mds: any) => mds?.status === 'OPENED'
      );
      setMultipleMdsAssessmentDates(
        getOpenEoc?.length && !!getOpenEoc[0]?.mdsAssessments
          ? constGetMdsAssessMentDate(getOpenEoc[0]?.mdsAssessments)
          : []
      );
    }
  }, [getMultipleMdsAssessment]);

  // Therapy weeks whose LAST day is on screen — those are the days that carry a CMG range
  // indicator, and each one needs its own verdict from the backend. A blue line on date B starts
  // a week, so the week before it runs B-7 … B-1. Disciplines usually share boundaries; the Map
  // collapses the duplicates so one week is fetched once.
  const cmgWeekRanges = useMemo(() => {
    const frequencyData = therapyPlanningSummary?.frequencyData;
    const windowStartDate = weekStartDate.format('YYYY-MM-DD');
    const ranges = new Map<string, { startDate: string; endDate: string }>();

    ['freqAlertPT', 'freqAlertOT', 'freqAlertSLP'].forEach((discipline: string) => {
      (frequencyData?.[discipline] ?? []).forEach((res: any) => {
        if (!res?.frequencyStartDate) return;
        const endDate = moment(res.frequencyStartDate).subtract(1, 'days').format('YYYY-MM-DD');
        // The week ended before the visible window starts, so it has no cell to sit on.
        if (endDate < windowStartDate) return;
        const startDate = moment(res.frequencyStartDate).subtract(7, 'days').format('YYYY-MM-DD');
        ranges.set(`${startDate}_${endDate}`, { startDate, endDate });
      });
    });

    return Array.from(ranges.entries()).map(([weekKey, range]) => ({ weekKey, ...range }));
  }, [therapyPlanningSummary, weekStartDate]);

  useEffect(() => {
    if (!therapyCaseMixTemplatesEnabled || !selectedPatient?.uuid) return;
    cmgWeekRanges.forEach((range) => {
      dispatch(
        getCmgStatus({
          patientId: selectedPatient?.uuid,
          abortToken: therapyPlanningAbort.token,
          weekKey: range?.weekKey,
          params: {
            startDate: range?.startDate,
            endDate: range?.endDate,
            // The backend resolves the verdict relative to today, so it has to be told which day
            // that is in the user's timezone.
            today: moment().format('YYYY-MM-DD'),
          },
        })
      );
    });
  }, [cmgWeekRanges, therapyCaseMixTemplatesEnabled, selectedPatient]);

  const getPerWeekNavigationAPI = () => {
    dispatch(
      getTherapyPlanningData_2_0({
        patientId: selectedPatient?.uuid,
        abortToken: therapyPlanningAbort.token,
        source: 'GET_WEEKLY',
        params: {
          providerId,
          startDate: weekStartDate.format('YYYY-MM-DD'),
          endDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
        },
      })
    );

    dispatch(
      getTherapyPlanningSummary_2_0({
        patientId: selectedPatient?.uuid,
        abortToken: therapyPlanningAbort.token,
        source: 'GET_WEEKLY',
        params: {
          providerId,
          startDate: weekStartDate.format('YYYY-MM-DD'),
          endDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
        },
      })
    );
    dispatch(
      getTherapyPlanningRangeAction({
        patientId: selectedPatient?.uuid,
        params: {
          startDate: weekStartDate.format('YYYY-MM-DD'),
          endDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
        },
      })
    );
    dispatch(
      getPayerDayCountAction({
        patientId: selectedPatient?.uuid,
        params: {
          startDate: weekStartDate.format('YYYY-MM-DD'),
          endDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
        },
      })
    );
    dispatch(
      get7DayLookBack({
        patientId: selectedPatient?.uuid,
        abortToken: therapyPlanningAbort.token,
        source: 'GET_WEEKLY',
        params: {
          providerId,
          today: moment().format('YYYY-MM-DD'),
          startDate: weekStartDate.format('YYYY-MM-DD'),
          endDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
        },
      })
    );
  };

  useEffect(() => {
    if (location.state?.from_mds === 'ARD_Tab') {
      getPerWeekNavigationAPI();
      return;
    }
    if (selectedPatient) {
      getPerWeekNavigationAPI();
    }

    return () => {
      therapyPlanningAbort.cancel('abort api');
      dispatch(resetTherapyPlanningData_2_0());
      dispatch(resetCmgStatus());
    };
  }, [weekStartDate, selectedPatient]);

  const [therapyPlaningNewResp, setTherapyPlaningNewResp] = useState<any>([]);
  const editSingleCellResponse = useSelector(
    (state: any) => state.editSingleCellOfTherapyPlanningReducer
  );

  useEffect(() => {
    if (!therapyPlanning_2_0?.isTherapyPlanning && !therapyPlanning_2_0?.response === null) return;

    if (!therapyPlanning_2_0?.isTherapyPlanning && !therapyPlanning_2_0?.response !== null) return;
    setTherapyPlaningNewResp(therapyPlanning_2_0?.therapyPlaningFormattedData);
  }, [therapyPlanning_2_0]);

  const progressNoteDue = useProgressNoteDue(
    selectedPatient?.uuid,
    therapyPlaningNewResp,
    editSingleCellResponse?.isCellEdited
  );

  const copyPasteTherapyPlanner = useSelector(
    (state: any) => state.copyPasteTherapyPlanningReducer_2_0
  );

  useEffect(() => {
    if (!copyPasteTherapyPlanner?.isCopyPaste && !copyPasteTherapyPlanner?.response === null)
      return;
    if (!copyPasteTherapyPlanner.isCopyPaste && !copyPasteTherapyPlanner.response !== null) {
      const refusal = serverRefusalMessage(copyPasteTherapyPlanner.response);
      if (refusal) {
        showRefusal(refusal, copyTherapyWeekActionRest);
        // The server may have pasted the days before the one it refused.
        getPerWeekNavigationAPI();
        return;
      }
      dispatch(copyTherapyWeekActionRest());
      return;
    }
    if (copyPasteTherapyPlanner.isCopyPaste) {
      setMessage(copyPasteTherapyPlanner.response);
      setIsSnackBarOpen(true);
      getPerWeekNavigationAPI();
      setTimeout(() => {
        setIsSnackBarOpen(false);
        dispatch(copyTherapyWeekActionRest());
        setMessage('');
      }, 3000);

      return;
    }
  }, [copyPasteTherapyPlanner]);

  // THIN-8321: OVERLAP opens its own modal in TherapyCard_2_0; any other refusal shows its message.
  useEffect(() => {
    if (editSingleCellResponse?.loading || editSingleCellResponse?.isCellEdited) return;
    if (editSingleCellResponse?.response?.code === 'OVERLAP') return;
    const refusal = serverRefusalMessage(editSingleCellResponse?.response);
    if (refusal) showRefusal(refusal, resetSingleCellReducer);
  }, [editSingleCellResponse]);

  useEffect(() => {
    if (!editSingleCellResponse?.isCellEdited && !editSingleCellResponse?.response === null) return;
    if (!editSingleCellResponse?.isCellEdited && !editSingleCellResponse?.response !== null) return;
    setTimeout(() => {
      dispatch(resetSingleCellReducer());
    }, 800);
  }, [editSingleCellResponse]);

  const [isWarning, setIsWarning] = useState(false);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);

  /** Shows a refusal from the server as a warning, then resets the slice that carried it. */
  const showRefusal = (refusal: string, reset: () => { type: string }) => {
    setMessage(refusal);
    setIsSnackBarOpen(true);
    setIsWarning(true);
    setTimeout(() => {
      setIsSnackBarOpen(false);
      setIsWarning(false);
      dispatch(reset());
      setMessage('');
    }, 3000);
  };

  useEffect(() => {
    if (location.state?.from_mds === 'ARD_Tab') return;
    if (
      weekStartDate.format('YYYY-MM-DD') !== moment().clone().startOf('week').format('YYYY-MM-DD')
    ) {
      setWeekStartDate(moment().clone().startOf('week'));
      setWeekEndDate(moment().clone().endOf('week'));
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (
      updateTherapyPlanningResponseDischargeDate?.isTherapyPlanningUpdated === false &&
      updateTherapyPlanningResponseDischargeDate?.response === null
    )
      return;
    if (
      updateTherapyPlanningResponseDischargeDate?.isTherapyPlanningUpdated === false &&
      updateTherapyPlanningResponseDischargeDate !== null
    ) {
      setMessage(updateTherapyPlanningResponseDischargeDate?.response?.message);
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setIsSnackBarOpen(false);
        dispatch(resetTherapyPlanningDischargeDate());
        setMessage('');
      }, 3000);
      return;
    } else {
      getPerWeekNavigationAPI();
      setMessage(updateTherapyPlanningResponseDischargeDate?.response?.message);
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setMessage('');
        setIsSnackBarOpen(false);
        dispatch(resetTherapyPlanningDischargeDate());
      }, 3000);
    }
  }, [updateTherapyPlanningResponseDischargeDate]);

  const applyTheTherapyTemplate = useSelector(
    (state: any) => state.applyTheTherapyTemplatesReducer
  );

  useEffect(() => {
    if (!applyTheTherapyTemplate?.isTemplateUpdated && applyTheTherapyTemplate?.response === null)
      return;
    if (!applyTheTherapyTemplate?.isTemplateUpdated && applyTheTherapyTemplate !== null) {
      setMessage(
        serverRefusalMessage(applyTheTherapyTemplate?.response) ||
          'Error while saving the templates'
      );
      getPerWeekNavigationAPI();
      setIsSnackBarOpen(true);
      setIsWarning(true);
      setTimeout(() => {
        setIsSnackBarOpen(false);
        setIsWarning(false);
        dispatch(resetTherapyTemplatesAction());
        setMessage('');
      }, 3000);
      return;
    }

    setMessage('The Template has been successfully applied');
    setIsSnackBarOpen(true);
    getPerWeekNavigationAPI();
    setTimeout(() => {
      setMessage('');
      setIsSnackBarOpen(false);
      dispatch(resetTherapyTemplatesAction());
    }, 3000);
  }, [applyTheTherapyTemplate]);

  const deleteMdsAssessmentDate = useSelector(
    (state: any) => state.deleteMdsAssessmentByDateReducer
  );

  useEffect(() => {
    if (
      deleteMdsAssessmentDate?.isMdsAssessmentDeleted === false &&
      deleteMdsAssessmentDate?.response === null
    )
      return;
    if (
      deleteMdsAssessmentDate?.isMdsAssessmentDeleted === false &&
      deleteMdsAssessmentDate !== null
    ) {
      setMessage(deleteMdsAssessmentDate?.response?.message);
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setIsSnackBarOpen(false);
        dispatch(resetDeleteMdsAssessmentByDate());
        setMessage('');
      }, 3000);
      return;
    } else {
      // getPerWeekNavigationAPI();
      setMessage(deleteMdsAssessmentDate?.response?.message);
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setMessage('');
        setIsSnackBarOpen(false);
        dispatch(resetDeleteMdsAssessmentByDate());
      }, 3000);
    }
  }, [deleteMdsAssessmentDate]);

  const applySelectedTherapyTem = useSelector(
    (state: any) => state.applySelectedTherapyPlanningTemplateReducer
  );

  useEffect(() => {
    if (!applySelectedTherapyTem?.isTemplateApply && applySelectedTherapyTem?.response === null)
      return;
    if (!applySelectedTherapyTem?.isTemplateApply && applySelectedTherapyTem !== null) {
      setMessage(
        serverRefusalMessage(applySelectedTherapyTem?.response) ||
          'Error while saving the templates'
      );
      getPerWeekNavigationAPI();
      setIsSnackBarOpen(true);
      setIsWarning(true);
      setTimeout(() => {
        setIsSnackBarOpen(false);
        setIsWarning(false);
        dispatch(resetApplySelectedTherapyTemplate());
        setMessage('');
      }, 3000);
      return;
    }

    setMessage('The Template has been successfully applied');
    setIsSnackBarOpen(true);
    getPerWeekNavigationAPI();
    setTimeout(() => {
      setMessage('');
      setIsSnackBarOpen(false);
      dispatch(resetApplySelectedTherapyTemplate());
    }, 3000);
  }, [applySelectedTherapyTem]);

  const getCurrentWeekDatesList = () => {
    const dates = [];

    const currentDate = weekStartDate.clone();
    const weekEndDateClone = weekStartDate.clone().add(8, 'days');
    while (currentDate.isSameOrBefore(weekEndDateClone, 'day')) {
      const formattedDate = currentDate.format('YYYY-MM-DD');
      dates.push(formattedDate);
      currentDate.add(1, 'day');
    }

    return dates;
  };

  const getCurrentWeekDates = () => {
    const dates = [];

    const currentDate = weekStartDate.clone();
    const weekEndDateClone = weekStartDate.clone().add(8, 'days');

    while (currentDate.isSameOrBefore(weekEndDateClone, 'day')) {
      const formattedDate = `${currentDate.format('ddd')} ${currentDate.format('M/DD')}`;
      const dateFormat = `${currentDate.format('YYYY-MM-DD')}`;
      dates.push({
        monthFormat: formattedDate,
        dateFormat: dateFormat,
      });
      currentDate.add(1, 'day');
    }

    return dates;
  };
  useEffect(() => {
    const list = getCurrentWeekDates();
    const list1 = getCurrentWeekDatesList();
    setCurrentDayList(list);
    setCurrentDayDataList(list1);
  }, [weekStartDate]);

  useEffect(() => {
    if (
      therapyPlanning_2_0?.totalTimeSummary &&
      therapyPlanning_2_0?.totalTimeSummary?.length === 9
    ) {
      const cloneUncoveredDaysResponse = Array.from(therapyPlanning_2_0?.totalTimeSummary);
      setUncoveredDay(
        cloneUncoveredDaysResponse
          ?.sort((a: any, b: any) => {
            const date1: any = new Date(a?.date);
            const date2: any = new Date(b?.date);
            return date1 - date2;
          })
          ?.map((list: any) => {
            return {
              isUncoveredDay: list?.uncovered || false,
            };
          })
      );
    } else {
      setUncoveredDay(uncoveredDumpyData);
    }
  }, [therapyPlanning_2_0]);

  const getSingleMds = useSelector((state: any) => state.getSingleMdsAssessmentReducer);
  const [mdsNavigateDate, setNavigateDate] = useState('');

  useEffect(() => {
    if (!getSingleMds?.getMdsAssessment && !getSingleMds?.response === null) return;
    if (!getSingleMds?.getMdsAssessment && !getSingleMds?.response !== null) return;
    if (getSingleMds?.getMdsAssessment) {
      navigate(`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.editDetails}`, {
        state: {
          action: 'Edit',
          selectedPatient: selectedPatient,
          from: 'ARD_Tab',
          ARD_Date: mdsNavigateDate,
          assessmentDetails: getSingleMds?.response?.data,
        },
      });
      return;
    }
  }, [getSingleMds]);

  useEffect(() => {
    if (!checkTherapyTemplate?.getSplitTask && checkTherapyTemplate?.response === null) return;
    if (!checkTherapyTemplate?.getSplitTask && checkTherapyTemplate?.response !== null) return;
    if (checkTherapyTemplate?.response?.message?.length) {
      setDeleteModalInfo({
        open: true,
        message:
          'This treatment plan currently includes split sessions. Changing the time will remove the split, reset the sessions duration, and unassign it. Do you want to continue?',
        saveLabel: 'Confirm',
        closeLabel: 'Cancel',
        title: 'Remove treatment split',
        deleteType: 'UPDATE_MIN_IN_PLANNER',
        errorType: 'WARNING',
      });
    } else {
      if (checkTherapyTemplate?.sourceTemp === 'APPLY_TEMPLATE') {
        dispatch(applySelectedTemplateAction(checkTherapyTemplate?.requestedPayload));
      } else if (checkTherapyTemplate?.sourceTemp === 'COPY_TEMPLATE') {
        dispatch(saveTherapyTemplatesAction(checkTherapyTemplate?.requestedPayload));
      }
      dispatch(resetCheckSplitMinInPlanner());
    }
  }, [checkTherapyTemplate]);

  const handleSaveSplitMin = () => {
    if (checkTherapyTemplate?.sourceTemp === 'APPLY_TEMPLATE') {
      dispatch(applySelectedTemplateAction(checkTherapyTemplate?.requestedPayload));
    } else if (checkTherapyTemplate?.sourceTemp === 'COPY_TEMPLATE') {
      dispatch(saveTherapyTemplatesAction(checkTherapyTemplate?.requestedPayload));
    }
    dispatch(resetCheckSplitMinInPlanner());
    handleCloseInfoModal();
  };

  const handleCloseInfoModal = () => {
    dispatch(resetCheckSplitMinInPlanner());
    setDeleteModalInfo((prevState: any) => ({ ...prevState, open: false }));
    setTimeout(() => {
      setDeleteModalInfo({
        message: '',
        saveLabel: '',
        closeLabel: '',
        title: '',
        deleteType: '',
        uuid: '',
        errorType: '',
      });
    }, 100);
  };

  return (
    <Box
      data-testid={therapyPlanningTestId('grid')}
      sx={{
        pointerEvents: disabledPlannerOnMds(
          dateOfServiceResponse?.guardrailType,
          dateOfServiceResponse?.isMdsPresent,
          dateOfServiceResponse?.gracePeriodDays,
          dateOfServiceResponse?.eocStartDate
        )
          ? 'none'
          : 'auto',
        ...(disabledPlannerOnMds(
          dateOfServiceResponse?.guardrailType,
          dateOfServiceResponse?.isMdsPresent,
          dateOfServiceResponse?.gracePeriodDays,
          dateOfServiceResponse?.eocStartDate
        ) && {
          '*': {
            pointerEvents: 'inherit !important',
          },
        }),
      }}
    >
      <Card sx={{ mt: 2 }}>
        <Stack>
          {dateOfServiceResponse?.guardrailType === 'BLOCK_UNTIL_MDS' &&
          !dateOfServiceResponse?.isMdsPresent ? (
            <Stack
              pl={2}
              gap={1}
              flexDirection="row"
              justifyContent={'start'}
              alignItems={'center'}
              sx={{ height: '44px', background: '#F790091F' }}
            >
              {' '}
              <AlertIcon />{' '}
              <Typography
                sx={{ color: '#B54708' }}
                variant="subtitle2"
              >
                MDS assessment is required - planning is blocked until the assessment is provided
              </Typography>
            </Stack>
          ) : dateOfServiceResponse?.guardrailType === 'LIMITED_TIME_GRACE' &&
            !dateOfServiceResponse?.isMdsPresent ? (
            <GracePeriodAlert />
          ) : null}
          {payerDay100Block && (
            <Stack
              pl={2}
              gap={1}
              flexDirection="row"
              justifyContent={'start'}
              alignItems={'center'}
              sx={{ height: '44px', background: '#F790091F' }}
              data-testid={therapyPlanningTestId('payer-day-100-banner')}
            >
              <AlertIcon />
              <Typography
                sx={{ color: '#B54708' }}
                variant="subtitle2"
              >
                {payerDay100BannerText(payerDay100Block)}
              </Typography>
            </Stack>
          )}

          <Stack
            sx={{ height: '52px' }}
            flexDirection="row"
          >
            <Stack
              sx={{ height: '100%', minWidth: '132px' }}
              flex={1.5}
            >
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  height: '100%',
                  padding: '8px 10px',
                  justifyContent: 'center',
                  borderRight: '1px solid #E5E7EB',
                  backgroundColor: '#F8F9FA',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    fontSize: '12px',
                    color: '#111927',
                    fontWeight: '500',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  Episode Start {formatEpisodeStartDate(dateOfServiceResponse?.eocStartDate)}
                </Box>
              </Stack>
              {/* <Box
                sx={{
                  borderRight: '1px solid #E5E7EB',
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: '#F8F9FA',
                  height: '10%',
                }}
              ></Box> */}
            </Stack>
            <Stack
              flexDirection="column"
              flex={8.5}
              sx={{ height: '100%' }}
            >
              <Stack
                flexDirection="row"
                sx={{
                  height: '100%',
                  display: 'flex',
                  borderBottom: '1px solid #F2F4F7',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {currentsDayList?.map((date: any, index: number) => (
                  <React.Fragment key={index}>
                    <Box
                      data-testid={dayTestId(date?.dateFormat)}
                      sx={{
                        width: `calc(100% / 9)`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        backgroundColor:
                          (uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay) ||
                          getMdsAssessmentRange?.[date?.dateFormat]
                            ? '#F2F4F7'
                            : 'white',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      key={index}
                    >
                      <Box
                        sx={{
                          fontSize: '12px',
                          borderRight: '1px solid #F2F4F7',
                          color:
                            date?.dateFormat === moment().format('YYYY-MM-DD')
                              ? '#6366F1'
                              : date?.dateFormat === assessmentDate
                              ? 'white'
                              : '#111927',
                          backgroundColor: date?.dateFormat === assessmentDate ? '#9d9d9d' : '',
                          borderRadius: '8px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                        }}
                      >
                        {date?.monthFormat}
                      </Box>
                      <UncoveredDay
                        selectedPatient={selectedPatient}
                        day={date}
                        mdsAssessments={getMdsAssessmentRange?.[date?.dateFormat]}
                        index={index}
                        uncoveredDay={uncoveredDay}
                        setNavigateDate={setNavigateDate}
                      />
                    </Box>
                  </React.Fragment>
                ))}
              </Stack>

              {/* <Stack
                flexDirection="row"
                sx={{
                  height: '35%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: '1px solid #F2F4F7',
                }}
              >
                {Array.from({ length: 9 }).map((_: any, index: any) => {
                  return (
                    <Box
                      key={index}
                      sx={{
                        width: `calc(100% / 7)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '500',
                        borderRight: '1px solid #F2F4F7',
                        height: '100%',
                        color: '#B5BCC4',
                        fontSize: macbookScreen ? '8px' : '10px',
                      }}
                    >
                      Planned | Actual
                    </Box>
                  );
                })}
              </Stack> */}
            </Stack>
          </Stack>
        </Stack>
      </Card>
      <TherapyCard_2_0
        therapyType="OT"
        therapyPlanningData_2_0={therapyPlaningNewResp?.otMap}
        progressNotesDue={progressNoteDue.OT}
        uncoveredDay={uncoveredDay}
        actualDischargeDate={getDischargeDatesOfTherapyPlanning2_0?.actualOtDischargeDate}
        dischargeDate={getDischargeDatesOfTherapyPlanning2_0?.anticipatedOtDischargeDate}
        summaryData={therapyPlanningSummary?.therapyPlanningSummaryWithDate?.OT}
        frequencyStartDate={therapyPlanningSummary?.frequencyData?.freqAlertOT}
        dosOfTherapyCard={
          dateOfServiceResponse?.dateOfServiceOt ? dateOfServiceResponse?.dateOfServiceOt : null
        }
      />
      <TherapyCard_2_0
        therapyType="PT"
        therapyPlanningData_2_0={therapyPlaningNewResp?.ptMap}
        progressNotesDue={progressNoteDue.PT}
        uncoveredDay={uncoveredDay}
        actualDischargeDate={getDischargeDatesOfTherapyPlanning2_0?.actualPtDischargeDate}
        dischargeDate={getDischargeDatesOfTherapyPlanning2_0?.anticipatedPtDischargeDate}
        summaryData={therapyPlanningSummary?.therapyPlanningSummaryWithDate?.PT}
        frequencyStartDate={therapyPlanningSummary?.frequencyData?.freqAlertPT}
        dosOfTherapyCard={
          dateOfServiceResponse?.dateOfServicePt ? dateOfServiceResponse?.dateOfServicePt : null
        }
      />
      <TherapyCard_2_0
        therapyType="SLP"
        therapyPlanningData_2_0={therapyPlaningNewResp?.slpMap}
        progressNotesDue={progressNoteDue.SLP}
        uncoveredDay={uncoveredDay}
        actualDischargeDate={getDischargeDatesOfTherapyPlanning2_0?.actualSlpDischargeDate}
        dischargeDate={getDischargeDatesOfTherapyPlanning2_0?.anticipatedSlpDischargeDate}
        summaryData={therapyPlanningSummary?.therapyPlanningSummaryWithDate?.SLP}
        frequencyStartDate={therapyPlanningSummary?.frequencyData?.freqAlertSLP}
        dosOfTherapyCard={
          dateOfServiceResponse?.dateOfServiceSlp ? dateOfServiceResponse?.dateOfServiceSlp : null
        }
      />
      <Card
        sx={{
          boxShadow: '0px 1px 10px 0px rgba(0, 0, 0, 0.15) !important',
          mb: 5,
          mt: 2,
        }}
      >
        <SummaryCard_2_0
          totalMins={therapyPlanning_2_0?.totalTimeSummary}
          variation={therapyPlanning_2_0?.totalTimeSummary}
          currentsWeekList={currentsDayDataList}
          lookBackSum={getTherapyPlanningLookBack?.get7DayLookBackSumData}
        />
      </Card>
      {isSnackBarOpen && (
        <SimpleSnackbar
          message={message}
          isWarning={isWarning}
          isError={
            updateTherapyPlanningResponse?.response !== null &&
            updateTherapyPlanningResponse?.isTherapyPlanningUpdated === false
          }
        />
      )}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={handleCloseInfoModal}
        aria-labelledby="customized-dialog-title"
        open={deleteModalInfo.open || false}
      >
        <DeletePopupModal
          close={handleCloseInfoModal}
          message={deleteModalInfo.message}
          button={{ saveLabel: deleteModalInfo.saveLabel, closeLabel: deleteModalInfo.closeLabel }}
          title={deleteModalInfo.title}
          handleSaveBtn={handleSaveSplitMin}
          errorType={deleteModalInfo?.errorType}
          testIds={confirmCancelTestIds('template-split-remove')}
        />
      </BootstrapDialog>
    </Box>
  );
};

export default WeekView_2_0;
